/* eslint-disable react/display-name */

import React, { FC, useCallback, useMemo, useState } from "react";
import { InputAdornment } from "@material-ui/core";

import BaseDataGrid from "../../../../../components/modules/BaseDataGrid";
import BaseInput from "../../../../../components/elements/BaseInput";
import Switch from "../../../../../components/elements/Switch";
import { Domain, PublicDomainListData } from "../../../../../types/campaignTypes";

const DomainList: FC<any> = ({ isDisabled, domainsInList, toggleSelectDomainLists, selectedDomainListsIds }) => {
  const [cpmValues, setCpmValues] = useState<Record<string, number>>(() =>
    domainsInList.reduce(
      (acc: any, d: any, index: any) => ({
        ...acc,
        [`${d?.beeswaxListItemId ? d.beeswaxListItemId : d.domainListId}-${index}`]: 25,
      }),
      {},
    ),
  );

  const updateCpm = useCallback(
    (row: any, newCpm: number) => {
      setCpmValues((prev) => ({
        ...prev,
        [row.id]: newCpm,
      }));

      toggleSelectDomainLists(
        {
          beeswaxListId: row.beeswaxListId,
          domain: row.domain,
          cpm: newCpm,
        },
        true,
      );
    },
    [toggleSelectDomainLists],
  );

  const handleDomainInListStatusChange = useCallback(
    (row: any) => () => {
      const cpm = cpmValues[row.id] ?? 25;

      toggleSelectDomainLists(
        {
          beeswaxListId: row.beeswaxListId,
          domain: row.domain,
          cpm,
        },
        !selectedDomainListsIds.includes(row.beeswaxListId),
      );
    },
    [cpmValues, selectedDomainListsIds, toggleSelectDomainLists],
  );

  const rows = useMemo(
    () =>
      domainsInList.map((domainInList: any, index: any) => ({
        id: `${domainInList.beeswaxListItemId}-${index}`,
        domain: domainInList.domain,
        beeswaxListId: domainInList.beeswaxListId,
      })),
    [domainsInList],
  );

  const renderEditCPM = useCallback(
    (params: any) => (
      <BaseInput
        autoFocus
        value={cpmValues[params.row.id]}
        onChange={(e) => {
          const val = Number(e.target.value) || 0;
          updateCpm(params.row, val);
        }}
        variant="outlined"
        name="cpm"
        height={38}
        label="CPM Bid"
        disabled={!isDisabled}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
      />
    ),
    [cpmValues, isDisabled, updateCpm],
  );

  const columns = useMemo(
    () => [
      { field: "domain", headerName: "Domain", flex: 1, disableColumnMenu: true },

      {
        field: "include",
        headerName: "Include",
        flex: 1,
        sortable: false,
        renderCell: (params: any) => (
          <Switch
            disabled={!isDisabled}
            checked={selectedDomainListsIds.includes(params.row.beeswaxListId)}
            onChange={handleDomainInListStatusChange(params.row)}
          />
        ),
      },

      {
        field: "cpm",
        headerName: "CPM Bid",
        flex: 1,
        editable: true,
        sortable: false,
        renderCell: (params: any) => <span>${cpmValues[params.row.id]}</span>,
        renderEditCell: renderEditCPM,
      },
    ],
    [isDisabled, selectedDomainListsIds, cpmValues, handleDomainInListStatusChange, renderEditCPM],
  );

  return <BaseDataGrid columns={columns} rows={rows} autoHeight disableSelectionOnClick />;
};

export default DomainList;
